import React, { useCallback, useMemo, useState } from 'react';

import { Box } from '@reckon-web/box';
import { Text } from '@reckon-web/text';
import { makeId } from '@reckon-web/utils';
import {
  SortDescriptor,
  Table,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableRow,
  createDefaultSort,
} from '@reckon-web/table';

import { DueDate } from '../../common/due-date';
import { StatusBadge } from '../../common/status-badge';
import { NamedUserAvatar } from '../../common/named-user-avatar';
import type { JobWithTasks } from '../../../hooks/use-query-jobs-with-tasks';
import { useOpenJobDialog } from '../../../hooks/use-open-job-dialog';
import { numberToCurrencyString } from '../../../utils/currency-utils';
import { CompletedTasks, IncompleteTasks, WorkItemTitle } from '../helpers';
import { useVisibleProperties } from '../../common/visible-properties';
import { JobStatusEnum } from '../../job/types/job-status-enum';

export const ListView: React.FC<{ jobs: JobWithTasks[]; testId?: string }> = ({
  jobs,
  testId = `jobs__list-view`,
}) => {
  const [{ jobList: jobListVisibleProperties }] = useVisibleProperties();
  const openJobDialog = useOpenJobDialog();
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: 'dueDate',
    direction: 'ascending',
  });

  const createSortHandler = useCallback(
    (column: string) => () => {
      setSortDescriptor((prevSort: SortDescriptor) => {
        const isActive = column === prevSort?.column;
        const direction =
          isActive && prevSort?.direction === 'ascending'
            ? 'descending'
            : 'ascending';
        return { column, direction };
      });
    },
    []
  );

  const getSortDirection = useCallback(
    (column: string) => {
      if (sortDescriptor) {
        return sortDescriptor.column === column
          ? sortDescriptor.direction
          : undefined;
      }
    },
    [sortDescriptor]
  );

  const sortedRows = useMemo(() => {
    if (sortDescriptor) {
      return jobs.sort(createDefaultSort(sortDescriptor));
    }
    return jobs;
  }, [jobs, sortDescriptor]);

  return (
    <Box paddingBottom="xxlarge">
      <Table
        aria-label="jobs list view"
        data-testId={makeId(testId, 'sorted-data-table')}
      >
        <TableHeader stickyOffset={0}>
          <TableColumnHeader
            onClick={createSortHandler('client.name')}
            aria-sort={getSortDirection('client.name')}
          >
            debtor
          </TableColumnHeader>
          <TableColumnHeader
            onClick={createSortHandler('name')}
            aria-sort={getSortDirection('name')}
          >
            job
          </TableColumnHeader>
          {jobListVisibleProperties.assignee && (
            <TableColumnHeader
              onClick={createSortHandler('assignee.fullName')}
              aria-sort={getSortDirection('assignee.fullName')}
            >
              assignee
            </TableColumnHeader>
          )}
          {jobListVisibleProperties.partner && (
            <TableColumnHeader
              onClick={createSortHandler('partner.fullName')}
              aria-sort={getSortDirection('partner.fullName')}
            >
              partner
            </TableColumnHeader>
          )}
          {jobListVisibleProperties.manager && (
            <TableColumnHeader
              onClick={createSortHandler('manager.fullName')}
              aria-sort={getSortDirection('manager.fullName')}
            >
              manager
            </TableColumnHeader>
          )}
          {jobListVisibleProperties.staff && (
            <TableColumnHeader
              onClick={createSortHandler('staff.fullName')}
              aria-sort={getSortDirection('staff.fullName')}
            >
              staff
            </TableColumnHeader>
          )}
          {jobListVisibleProperties.status && (
            <TableColumnHeader
              onClick={createSortHandler('status.orderIndex')}
              aria-sort={getSortDirection('status.orderIndex')}
            >
              status
            </TableColumnHeader>
          )}
          {jobListVisibleProperties.dueDate && (
            <TableColumnHeader
              onClick={createSortHandler('dueDate')}
              aria-sort={getSortDirection('dueDate')}
            >
              due date
            </TableColumnHeader>
          )}
          {jobListVisibleProperties.budget && (
            <TableColumnHeader
              onClick={createSortHandler('budget')}
              aria-sort={getSortDirection('budget')}
            >
              budget
            </TableColumnHeader>
          )}
          {jobListVisibleProperties.tasks && (
            <TableColumnHeader
              onClick={createSortHandler('tasks.length')}
              aria-sort={getSortDirection('tasks.length')}
            >
              tasks
            </TableColumnHeader>
          )}
        </TableHeader>

        <TableBody>
          {sortedRows.map((job) => {
            return (
              <TableRow key={job.cloudId}>
                {/* debtor */}
                <TableCell>{job.client?.name}</TableCell>

                {/* job */}
                <TableCell>
                  <WorkItemTitle
                    name={job.name}
                    locked={
                      job.status.status === JobStatusEnum.valuesEnum.Closed
                    }
                    onClick={() => openJobDialog(job.cloudId)}
                    testId={makeId(testId, 'job')}
                    weight="regular"
                  />
                </TableCell>

                {/* assignee */}
                {jobListVisibleProperties.assignee && (
                  <TableCell>
                    {job.assignee && (
                      <NamedUserAvatar
                        user={job.assignee}
                        avatarSize="xsmall"
                        textSize="small"
                        testId={makeId(testId, 'assignee')}
                      />
                    )}
                  </TableCell>
                )}

                {/* partner */}
                {jobListVisibleProperties.partner && (
                  <TableCell>
                    {job.partner && (
                      <NamedUserAvatar
                        user={job.partner}
                        avatarSize="xsmall"
                        textSize="small"
                        testId={makeId(testId, 'partner')}
                      />
                    )}
                  </TableCell>
                )}

                {/* manager */}
                {jobListVisibleProperties.manager && (
                  <TableCell>
                    {job.manager && (
                      <NamedUserAvatar
                        user={job.manager}
                        avatarSize="xsmall"
                        textSize="small"
                        testId={makeId(testId, 'manager')}
                      />
                    )}
                  </TableCell>
                )}

                {/* staff */}
                {jobListVisibleProperties.staff && (
                  <TableCell>
                    {job.staff && (
                      <NamedUserAvatar
                        user={job.staff}
                        avatarSize="xsmall"
                        textSize="small"
                        testId={makeId(testId, 'staff-avatar')}
                      />
                    )}
                  </TableCell>
                )}

                {/* status */}
                {jobListVisibleProperties.status && (
                  <TableCell>
                    {job.status && (
                      <StatusBadge
                        label={job.status.name}
                        colour={job.status.colour}
                        data-testid={makeId(testId, 'status-badge')}
                      />
                    )}
                  </TableCell>
                )}

                {/* due date */}
                {jobListVisibleProperties.dueDate && (
                  <TableCell>
                    {job.dueDate && (
                      <DueDate
                        date={job.dueDate}
                        showDueDays={
                          job.status.status !==
                            JobStatusEnum.valuesEnum.Closed &&
                          job.status.status !==
                            JobStatusEnum.valuesEnum.Completed
                        }
                        testId={makeId(testId, 'due-date')}
                      />
                    )}
                  </TableCell>
                )}

                {/* budget */}
                {jobListVisibleProperties.budget && (
                  <TableCell>
                    {typeof job.budget === 'number' && (
                      <Text
                        size="small"
                        data={{ testid: makeId(testId, 'budget') }}
                      >
                        {numberToCurrencyString(job.budget)}
                      </Text>
                    )}
                  </TableCell>
                )}

                {/* tasks */}
                {jobListVisibleProperties.tasks && (
                  <TableCell>
                    {job.tasks &&
                      job.tasks.length > 0 &&
                      (() => {
                        switch (job.status.status) {
                          case null:
                          case undefined:
                          case JobStatusEnum.valuesEnum.NotStarted:
                          case JobStatusEnum.valuesEnum.InProgress:
                          case JobStatusEnum.valuesEnum.Completed:
                            return (
                              <IncompleteTasks
                                counts={job.taskCountByStatus}
                                totalCount={job.tasks.length}
                                testId={makeId(testId, 'incomplete-tasks')}
                              />
                            );

                          case JobStatusEnum.valuesEnum.Closed:
                            return (
                              <CompletedTasks
                                counts={job.taskCountByStatus}
                                totalCount={job.tasks.length}
                                testId={makeId(testId, 'complete-tasks')}
                              />
                            );
                        }
                      })()}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};
